import { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { LeftOutlined, CaretRightFilled } from "@ant-design/icons";
import { Badge, Card, Col, Row, notification } from "antd";
import Icon from "@ant-design/icons";
import {
  ToteIcon,
  TrolleyIcon,
  PriorityIcon,
  LeftIcon,
} from "../../assets/icons/icons";
import Header from "../../components/layout/header";
import UniversalScanButton from "../../components/mobile/UniversalScanButton";
import ToteConfirmationModal from "../../components/mobile/ToteConfirmationModal";
import SuccessAlert from "../../components/mobile/SuccessAlert";
import TrolleyLocationConfirmationModal from "../../components/mobile/TrolleyLocationConfirmationModal";
import { api } from "../../services/api";
import { useTrolleyById, useToteById, useLocations } from "../common/queries";
import type { LocationItem, Tote } from "../../services/types";
import {
  useLoadToteToTrolley,
  useUpdateTrolleyLocation,
} from "../common/mutations";
import { getTrolleyStatus } from "../../components/reusable/styles";
import Styles from "./trolleyInfo.module.scss";
import axios from "axios";
import { convertUTCToLocal } from "../../utils/timeUtils";
import {
  isFALPoULocation,
  isAirlogEmptyHoldingLocation,
  hasTotesLoaded,
  isTrolleyEmpty,
} from "../../utils/locationUtils";

const TrolleyInfo = () => {
  const { id: encodedId } = useParams<{ id: string }>();
  // Decode the ID to handle special characters like forward slashes
  const id = encodedId ? decodeURIComponent(encodedId) : undefined;
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // React Query hooks
  const {
    data: trolleyData,
    isLoading,
    error,
    refetch: refetchTrolley,
  } = useTrolleyById(id);
  const { data: toteData } = useToteById(trolleyData?.toteId || undefined);
  const { data: locationsData } = useLocations();
  const loadToteMutation = useLoadToteToTrolley();
  const updateLocationMutation = useUpdateTrolleyLocation();

  const [isToteModalOpen, setIsToteModalOpen] = useState(false);
  const [isLocationModalOpen, setIsLocationModalOpen] = useState(false);
  const [isSuccessOpen, setIsSuccessOpen] = useState(false);
  const [scannedToteCode, setScannedToteCode] = useState("");
  const [scannedLocationCode, setScannedLocationCode] = useState("");
  const [selectedLocation, setSelectedLocation] = useState<LocationItem | null>(
    null
  );
  const [isCheckingLocation, setIsCheckingLocation] = useState(false);
  const [locationErrorMessage, setLocationErrorMessage] = useState<
    string | null
  >(null);
  const [successTitle, setSuccessTitle] = useState("");
  const [successMessage, setSuccessMessage] = useState<React.ReactNode>("");
  const [buttonText, setButtonText] = useState("Done");
  const [isCheckingTote, setIsCheckingTote] = useState(false);
  const [pendingToteData, setPendingToteData] = useState<Tote | null>(null);
  const [secondaryLocationActions, setSecondaryLocationActions] = useState<
    Map<string, "In" | "Out">
  >(new Map());

  const allowedLoadStatuses = ["empty - available", "loading"];
  const allowedLoadLocations = [
    "airlog packing",
    "airlog dispatch dock",
    "fal proximity store (l80)",
    "fal proximity store",
    "fal pou",
  ];

  const canLoadTote = Boolean(
    trolleyData?.status &&
      allowedLoadStatuses.includes(trolleyData.status.toLowerCase()) &&
      (!trolleyData?.primaryLocation ||
        allowedLoadLocations.some((loc) =>
          trolleyData.primaryLocation?.toLowerCase().includes(loc)
        ))
  );
  const PENDING_TOTE_LOAD_KEY = "pendingToteLoadContext";

  const clearPendingLoadContext = useCallback(() => {
    sessionStorage.removeItem(PENDING_TOTE_LOAD_KEY);
  }, []);

  const persistPendingLoadContext = useCallback(
    (payload: { trolleyId: string; toteId: string }) => {
      sessionStorage.setItem(PENDING_TOTE_LOAD_KEY, JSON.stringify(payload));
    },
    []
  );

  useEffect(() => {
    if (error && !isLoading && id) {
      const errorMessage =
        error instanceof Error ? error.message : "Trolley not found";
      notification.info({
        message: "Trolley Not Found",
        description: `Trolley ID: ${id} is not registered in our system`,
        duration: 2,
      });
      // Navigate to register flow when trolley is not found
      const timer = setTimeout(() => {
        navigate(`/register-trolley-error?trolleyId=${encodeURIComponent(id)}`);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [error, isLoading, navigate, id]);

  const handleToteScanned = async (code: string) => {
    if (!trolleyData) {
      return true;
    }

    if (!canLoadTote) {
      notification.warning({
        message: "Action Not Allowed",
        description:
          "This trolley is not in a status that allows loading totes.",
        duration: 4,
      });
      return true;
    }

    const trimmedCode = code.trim();
    if (!trimmedCode) {
      notification.error({
        message: "Invalid Tote ID",
        description: "Please scan a valid tote ID.",
      });
      return false;
    }

    setIsCheckingTote(true);
    setPendingToteData(null);

    try {
      const response = await api.checkTote({
        tote_id: trimmedCode,
      });

      // Check if tote is registered (has tote_details or tote_id in response)
      if (
        response.status_code === 200 &&
        (response.tote_details?.tote_id || response.tote_details?.id)
      ) {
        // Extract the record ID from checkTote response
        const toteRecordId = response.tote_details?.id;

        // Use the record ID to fetch full tote info, fallback to trimmedCode if record ID not available
        const toteIdToFetch = toteRecordId ? String(toteRecordId) : trimmedCode;
        let toteInfo = await api.getToteById(toteIdToFetch);

        // Ensure recordId is set from checkTote response if not already set
        if (toteRecordId && !toteInfo.recordId) {
          toteInfo = { ...toteInfo, recordId: toteRecordId };
        }

        const toteStatus = toteInfo.activityStatus?.toLowerCase() || "";

        // Check if tote is already loaded to a trolley
        if (toteStatus === "loaded to trolley" || toteInfo.isLoadedToTrolley) {
          notification.error({
            message: "Tote Already Loaded",
            description:
              "You can't load this tote — it's already on a trolley.",
            duration: 4,
          });
          return true;
        }

        // Check if tote journey is completed
        if (toteStatus === "completed") {
          notification.error({
            message: "Tote Journey Completed",
            description:
              "Unable to proceed. This tote has already reached its final destination.",
            duration: 4,
          });
          return true;
        }

        // Check if tote is available or unloaded from trolley
        if (
          toteStatus === "available" ||
          toteStatus === "unloaded from trolley"
        ) {
          clearPendingLoadContext();
          setPendingToteData(toteInfo);
          setScannedToteCode(trimmedCode);
          setIsToteModalOpen(true);
          return true;
        }

        // For any other status, show generic not available message
        notification.warning({
          message: "Tote Not Available",
          description: "This tote is not available for loading.",
          duration: 4,
        });
        return true;
      }

      // Tote is not registered - navigate to register flow
      notification.info({
        message: "Tote Not Found",
        description: `Tote ID: ${trimmedCode} is not registered in our system`,
      });

      persistPendingLoadContext({
        trolleyId: trolleyData.id,
        toteId: trimmedCode,
      });

      setTimeout(() => {
        navigate(
          `/register-tote-error?toteId=${encodeURIComponent(trimmedCode)}`
        );
      }, 500);

      return true;
    } catch (error) {
      const description = axios.isAxiosError(error)
        ? error.response?.data?.detail || error.response?.data?.message
        : undefined;
      notification.info({
        message: "Tote Not Found",
        description: description || "Tote not found or unable to verify",
      });
      return true;
    } finally {
      setIsCheckingTote(false);
    }
  };

  useEffect(() => {
    if (!trolleyData?.id || isLoading) {
      return;
    }

    try {
      const stored = sessionStorage.getItem(PENDING_TOTE_LOAD_KEY);
      if (!stored) {
        return;
      }

      const parsed = JSON.parse(stored) as {
        trolleyId: string;
        toteId: string;
      } | null;

      if (parsed && parsed.trolleyId === trolleyData.id) {
        void (async () => {
          try {
            setIsCheckingTote(true);
            // parsed.toteId should now be the record ID from registration
            const toteInfo = await api.getToteById(parsed.toteId);
            setPendingToteData(toteInfo);
            // Use toteNo (string ID) for display, but keep record ID for API calls
            setScannedToteCode(toteInfo.toteNo || parsed.toteId);
            setIsToteModalOpen(true);
          } catch (error) {
            console.warn("Failed to resume tote load context", error);
            notification.error({
              message: "Unable to resume load",
              description: "Please rescan the tote to continue loading.",
            });
            setPendingToteData(null);
            setScannedToteCode("");
          } finally {
            setIsCheckingTote(false);
            clearPendingLoadContext();
          }
        })();
      }
    } catch (error) {
      console.warn("Failed to parse pending tote load context", error);
      clearPendingLoadContext();
    }
  }, [trolleyData?.id, isLoading, clearPendingLoadContext]);

  // No need for confirmation handling - warning page handles the API call directly

  const handleLocationScanned = async (code: string): Promise<boolean> => {
    const trimmedCode = code.trim();
    if (!trimmedCode) {
      notification.info({
        message: "Location Not Found",
        description: `Scanned location "${trimmedCode}" is not available in the system`,
      });
      return false;
    }

    setLocationErrorMessage(null);
    setIsCheckingLocation(true);

    try {
      let matchedLocation = locationsData?.locations?.find(
        (loc) =>
          loc.location_barcode?.toLowerCase() === trimmedCode.toLowerCase()
      );

      if (!matchedLocation) {
        try {
          const freshLocations = await api.getLocations();
          matchedLocation = freshLocations.locations?.find(
            (loc) =>
              loc.location_barcode?.toLowerCase() === trimmedCode.toLowerCase()
          );
        } catch (fetchError) {
          console.error("Failed to fetch locations for validation", fetchError);
        }
      }

      if (!matchedLocation) {
        setSelectedLocation(null);
        notification.info({
          message: "Location Not Found",
          description: `Scanned location "${trimmedCode}" is not available in the system`,
        });
        return false;
      }

      setSelectedLocation(matchedLocation);
      setScannedLocationCode(matchedLocation.location_barcode);
      setIsLocationModalOpen(true);
      return true;
    } finally {
      setIsCheckingLocation(false);
    }
  };

  const handleLocationConfirm = async (locationType?: "In" | "Out") => {
    if (!trolleyData || !selectedLocation) {
      return;
    }

    const finalLocationType = locationType || "In";

    // Check if this is a secondary location and if action was already performed
    if (selectedLocation.secondary_location) {
      const secondaryKey = `${selectedLocation.primary_location.toLowerCase()}|${selectedLocation.secondary_location.toLowerCase()}`;
      const performedAction = secondaryLocationActions.get(secondaryKey);

      if (performedAction === finalLocationType) {
        notification.error({
          message: "Action Already Performed",
          description: "Action already performed for this location.",
          duration: 3,
        });
        return;
      }
    }

    // Validation for special locations
    const primaryLocation = selectedLocation.primary_location;
    const trolleyStatus = trolleyData.status;

    // Case 1: Moving OUT of FAL PoU with totes loaded
    if (
      finalLocationType === "Out" &&
      isFALPoULocation(primaryLocation) &&
      hasTotesLoaded(trolleyStatus)
    ) {
      // Navigate to warning page
      const params = new URLSearchParams({
        trolleyId: trolleyData.id,
        locationBarcode: selectedLocation.location_barcode,
        primaryLocation: selectedLocation.primary_location,
        secondaryLocation: selectedLocation.secondary_location || "",
        from: "trolley",
      });
      navigate(`/trolley-location-warning?${params.toString()}`);
      return;
    }

    // Case 2: Moving IN/OUT of Airlog Empty Holding with totes loaded
    if (
      isAirlogEmptyHoldingLocation(primaryLocation) &&
      hasTotesLoaded(trolleyStatus)
    ) {
      // Show error notification and return
      notification.error({
        message: "Action Not Allowed",
        description: "The Trolley isn't empty. Please unload all Totes before entering or exiting the Airlog Empty Holding Area.",
        duration: 5,
      });
      return;
    }

    try {
      await updateLocationMutation.mutateAsync({
        trolleyId: trolleyData.id,
        locationBarcode: selectedLocation.location_barcode,
        locationType: finalLocationType,
      });

      // Track the action for secondary locations
      if (selectedLocation.secondary_location) {
        const secondaryKey = `${selectedLocation.primary_location.toLowerCase()}|${selectedLocation.secondary_location.toLowerCase()}`;
        setSecondaryLocationActions((prev) => {
          const newMap = new Map(prev);
          newMap.set(secondaryKey, finalLocationType);
          return newMap;
        });
      }

      setIsLocationModalOpen(false);
      setIsSuccessOpen(true);
      const action = finalLocationType === "Out" ? "Out" : "In";
      setSuccessTitle(
        action === "In"
          ? "Trolley Location Updated"
          : "Trolley Dispatched Successfully"
      );
      setSuccessMessage(
        action === "In" ? (
          <>
            Trolley is successfully assigned to{" "}
            <b style={{ color: "#101010" }}>
              {selectedLocation.primary_location} (In)
              {selectedLocation.secondary_location
                ? ", " + selectedLocation.secondary_location
                : ""}
            </b>
          </>
        ) : (
          <>
            Trolley dispatched from Location{" "}
            <b style={{ color: "#101010" }}>
              {selectedLocation.primary_location} (Out)
              {selectedLocation.secondary_location
                ? ", " + selectedLocation.secondary_location
                : ""}
            </b>
          </>
        )
      );
      setButtonText("Go to Trolley Information");
    } catch (error) {
      console.error("Failed to update trolley location", error);
      const errorDescription = axios.isAxiosError(error)
        ? error.response?.data?.detail || error.response?.data?.message || error.message
        : error instanceof Error
        ? error.message
        : "Failed to update location. Please try again.";
      
      // Check if error is related to invalid location
      const errorLower = errorDescription.toLowerCase();
      const isLocationError = 
        errorLower.includes("location") && 
        (errorLower.includes("not found") || 
         errorLower.includes("invalid") || 
         errorLower.includes("not available") ||
         errorLower.includes("does not exist"));
      
      const locationBarcode = selectedLocation?.location_barcode || scannedLocationCode || "";
      
      if (isLocationError) {
        notification.info({
          message: "Location Not Found",
          description: `Scanned location "${locationBarcode}" is not available in the system`,
        });
      } else {
        notification.error({
          message: "Update Failed",
          description: errorDescription,
          duration: 5,
        });
      }
      
      // Close the modal on error
      setIsLocationModalOpen(false);
      setSelectedLocation(null);
      setScannedLocationCode("");
      setLocationErrorMessage(null);
    }
  };

  const handleLocationCancel = () => {
    setIsLocationModalOpen(false);
    setScannedLocationCode("");
    setSelectedLocation(null);
    setLocationErrorMessage(null);
  };

  const handleToteConfirm = async () => {
    if (!trolleyData || !scannedToteCode) return;

    // Ensure we have the record ID - it should be in pendingToteData from getToteById
    if (!pendingToteData?.recordId) {
      notification.error({
        message: "Unable to load tote",
        description:
          "Tote record ID not found. Please try scanning the tote again.",
        duration: 4,
      });
      return;
    }

    try {
      const response = await loadToteMutation.mutateAsync({
        toteId: scannedToteCode,
        trolleyId: trolleyData.id,
        toteRecordId: pendingToteData.recordId, // Use the record ID from getToteById
      });
      clearPendingLoadContext();
      setSuccessTitle("Tote Loaded to Trolley");
      setSuccessMessage(
        response?.message || (
          <>
            Tote (ID: <b style={{ color: "#101010" }}>{scannedToteCode}</b>)
            successfully loaded to Trolley
          </>
        )
      );
      setButtonText("Go to Trolley Information");
      setIsToteModalOpen(false);
      setPendingToteData(null);
      setScannedToteCode("");
      setIsSuccessOpen(true);
      await refetchTrolley();
    } catch (error) {
      const description = axios.isAxiosError(error)
        ? error.response?.data?.detail || error.response?.data?.message
        : undefined;
      notification.error({
        message: "Failed to load the Tote to the Trolley! Try again later.",
        description,
        duration: 5,
      });
    }
  };

  const handleToteCancel = () => {
    setIsToteModalOpen(false);
    setScannedToteCode("");
    setPendingToteData(null);
  };

  const handleSuccessClose = async () => {
    setIsSuccessOpen(false);
    setSelectedLocation(null);
    setScannedLocationCode("");
    setLocationErrorMessage(null);
    if (trolleyData?.id) {
      await refetchTrolley();
      navigate(`/trolley-info/${encodeURIComponent(trolleyData.id)}`);
    }
  };

  if (isLoading || !trolleyData) {
    return (
      <div className={Styles.mainContainer}>
        <Header />
        <div className={Styles.loadingContainer}>Loading...</div>
      </div>
    );
  }

  // Check if any tote has High priority
  const hasHighPriorityTote = Boolean(
    trolleyData.totes?.some(
      (tote) => tote.priority && tote.priority.toLowerCase() === "high"
    ) || trolleyData.priority
  );

  return (
    <div className={Styles.mainContainer}>
      <Header />
      <div className={Styles.contentContainer}>
        <Row
          align="middle"
          justify="space-between"
          style={{ marginBottom: "10px" }}
        >
          <Icon
            component={LeftIcon}
            onClick={() => navigate("/operator-landing")}
          />
          <div className={Styles.title}>Trolley Information</div>
          <div />
        </Row>

        <div className={Styles.infoSection}>
          <div className={Styles.sectionTitle}>Trolley Info</div>
          <Card className={Styles.infoCard}>
            <div className={Styles.trolleyHeader}>
              <div className={Styles.trolleyInfoRow}>
                <Icon component={TrolleyIcon} className={Styles.icon} />
                <div>
                  <div className="labels">Trolley ID</div>
                  <div className="values">{trolleyData.id}</div>
                </div>
              </div>
              {hasHighPriorityTote && (
                <div className="priorityTag">
                  <Icon component={PriorityIcon} />
                  <span>Priority</span>
                </div>
              )}
            </div>

            <Row gutter={[16, 16]} className={Styles.detailsGrid}>
              <Col span={12}>
                <div className="labels">MDU</div>
                <div className={Styles.detailValue}>
                  {trolleyData.mdu || "N/A"}
                </div>
              </Col>
              <Col span={12}>
                <div className="labels">Status</div>
                <div
                  className={Styles.detailValue}
                  style={{ color: getTrolleyStatus(trolleyData.status) }}
                >
                  {trolleyData.status}
                </div>
              </Col>
            </Row>
            <Row gutter={[16, 16]} className={Styles.detailsGrid}>
              <Col span={12}>
                <div className="labels">Registered Date</div>
                <div className={Styles.detailValue}>
                  {trolleyData.registeredDate
                    ? convertUTCToLocal(trolleyData.registeredDate)
                    : "N/A"}
                </div>
              </Col>
            </Row>

            <div className={Styles.locationInfo}>
              <div className="labels">Last Scanned Location</div>
              <div className={Styles.detailValue}>
                {trolleyData?.lastScannedLocation || "N/A"}
              </div>
            </div>
          </Card>
        </div>

        <div className={Styles.infoSection}>
          <div className={Styles.sectionTitle}>
            Tote
            {((trolleyData.totes && trolleyData.totes.length > 1) || 
              (trolleyData.toteIds && trolleyData.toteIds.length > 1)) ? "s" : ""}
          </div>
          {((trolleyData.totes && trolleyData.totes.length > 0) || 
            (trolleyData.toteIds && trolleyData.toteIds.length > 0)) ? (
            <>
              {(trolleyData.totes || trolleyData.toteIds?.map(id => ({ tote_id: id })) || []).map((tote, index) => {
                const toteId = tote.tote_id;
                const priority = tote.priority;
                const totalTotes = trolleyData.totes?.length || trolleyData.toteIds?.length || 0;
                
                return (
                  <Card
                    key={toteId}
                    className={Styles.infoCard}
                    style={{
                      marginBottom: index < totalTotes - 1 ? "12px" : "0",
                    }}
                  >
                    <Row justify="space-between" className={Styles.trolleyHeader}>
                      <div className={Styles.trolleyInfoRow}>
                        <Icon component={ToteIcon} className={Styles.icon} />
                        <div>
                          <div className={Styles.label}>Tote No</div>
                          <div className={Styles.value}>{toteId}</div>
                        </div>
                      </div>
                      {priority && priority.toLowerCase() === "high" && (
                        <div className={Styles.priorityBadge}>
                          <Icon component={PriorityIcon} />
                        </div>
                      )}
                    </Row>
                  </Card>
                );
              })}
            </>
          ) : (
            <Card className={Styles.infoCard}>
              <Row className={Styles.trolleyHeader}>
                <div className={Styles.trolleyInfoRow}>
                  <Icon component={ToteIcon} className={Styles.icon} />
                  <div className={Styles.value}>
                    No Totes have been loaded into Trolley
                  </div>
                </div>
              </Row>
            </Card>
          )}
        </div>
      </div>

      <UniversalScanButton
        mode="trolley-info"
        onToteScanned={handleToteScanned}
        onLocationScanned={handleLocationScanned}
        isCheckingLocation={isCheckingLocation}
        isCheckingTote={isCheckingTote}
        scanErrorMessage={locationErrorMessage}
        onScanErrorClear={() => setLocationErrorMessage(null)}
        availableActions={canLoadTote ? ["tote", "location"] : ["location"]}
      />

      {scannedToteCode && (
        <ToteConfirmationModal
          open={isToteModalOpen}
          toteId={scannedToteCode}
          onConfirm={handleToteConfirm}
          onCancel={handleToteCancel}
          isLoading={loadToteMutation.isPending}
          toteDataOverride={pendingToteData}
        />
      )}

      <SuccessAlert
        open={isSuccessOpen}
        title={successTitle}
        message={successMessage}
        buttonText={buttonText}
        onClose={handleSuccessClose}
      />

      {isLocationModalOpen && selectedLocation && (
        <TrolleyLocationConfirmationModal
          open={isLocationModalOpen}
          location={{
            primary: selectedLocation.primary_location,
            secondary: selectedLocation.secondary_location,
            barcode: selectedLocation.location_barcode,
            status: selectedLocation.current_location_type ?? null,
          }}
          lastScannedLocation={trolleyData.lastScannedLocation}
          lastLocationStatus={trolleyData.currentLocationType ?? null}
          onConfirm={handleLocationConfirm}
          onCancel={handleLocationCancel}
          isLoading={updateLocationMutation.isPending}
          secondaryLocationActions={secondaryLocationActions}
        />
      )}
    </div>
  );
};

export default TrolleyInfo;
